import * as THREE from "three";
import gsap from "gsap";
import Stats from "three/examples/jsm/libs/stats.module.js";
import StageManager from "../components/StageManager";
import PixiManager from "../components/PixiManager";
import SoundManager from "../components/SoundManager";
import HapticManager from "../components/HapticManager";
import UiManager from '../components/UiManager';
import { GameConfig } from "../utils/GameConfig";
import LocaleManager from "./LocaleManager";
import config from "config";
import AssetsManager from "./AssetsManager";
import tools from "tools";

class GameManager {
    constructor() {
        this._gameStatus = {
            // The frame rate of the game
            FPS: 60,

            // Is the game run on debug mode
            isDebug: false,

            // Is the game is paused
            isPaused: false,

            // Is the player loss the game
            isLose: false,

            // Is the player loss the first time
            isFirstLose: true,

            // Is the player has clicked the first time
            isFirstClick: false,

            // Is the player has clicked the retry button
            isRetryClick: false,
        };
        this._nbGamePlayed = 0;

        this._onMove = this._onMove.bind(this);
        this._onDown = this._onDown.bind(this);

        this.setup = this.setup.bind(this);
        this.update = this.update.bind(this);
        this._keydown = this._keydown.bind(this);
        this._keyup = this._keyup.bind(this);
        this._rewardHandler = this._rewardHandler.bind(this);
        this._noAdHandler = this._noAdHandler.bind(this);
        this.changeColor = this.changeColor.bind(this);

        // Html dom elements
        this._canvas = document.querySelector(".canvas");

        this._scoreUi = document.querySelector('.js-score');
        this._bestScoreUi = document.querySelector('.js-best-score');
        this._newBestScore = document.querySelector('.js-new-best-score');
        this._newBestText = document.querySelector('.js-new-best-text');

        this._startScreen = document.querySelector(".start-screen");
        this._retryScreen = document.querySelector(".retry-screen");
        this._endScreen = document.querySelector(".end-screen");
        this._playText = document.querySelector(".play-text");

        this._noVideoCTA = document.querySelector(".js-no-video");
        this._retryVideo = document.querySelector(".video-replay");
        this._circleSvg = document.querySelector(".js-circle-svg");
        this._lockRetry = false;

        this._privacyIcon = document.querySelector(".js-privacy");

        this._scoreContainer = document.querySelector(".score-container");
        this._bestScoreGame = document.querySelector(".best-score");
        this._bestScoreEndScreen = document.querySelector(".new-best-score");

        this._backgroundGradient = document.querySelector(".background-gradient");

        this._isMobile = tools.setupIs().mobile;

    }

    /// Readable variables

    // HDML 5 SDK of the game
    get H5SDK() {
        return window.H5SDK;
    }

    // Status of the game
    get GAME() {
        return this._gameStatus;
    }

    // Event dispatcher of the game
    get EVENT_DISPATCHER() {
        return this._eventDispatcher;
    }

    /// Readable and writable variables

    // Score of the game
    get SCORE() {
        return this._score;
    }
    set SCORE(value) {
        this._score = value;
        this._scoreUi.innerHTML = this._score;
        // If better than the best score change and save the best score
        if (this._score > this.BEST_SCORE) {
            this.BEST_SCORE = this._score;

            console.log("happyTimeShowed : ", GameConfig.happyTimeShowed);
            if (!GameConfig.happyTimeShowed && GameConfig.startedBestScore > 0) {
                GameConfig.happyTimeShowed = true;
                console.log("*** happyTime ***");
               // this.H5SDK.getPlatform().happyTime() && this.H5SDK.getPlatform().happyTime();
            }

        }

        // Update the score for the pixi manager
        this.pixiManager && this.pixiManager.updateScore(this._score);


    }

    // Best score of the game
    get BEST_SCORE() {
        return this._bestScore;
    }
    set BEST_SCORE(value) {
        this._bestScore = value;

        this._bestScoreUi.innerHTML = this._bestScore;


        this._newBestScore.innerHTML = this._score;
        // this._newBestText.innerHTML = LocaleManager.words.score;

        this._bestScoreGame.innerText = LocaleManager.words.best + this._bestScore;
    }

    // Coin of the game
    get COIN() {
        return this._coin;
    }
    set COIN(value) {
        if (value == null || value == "") {
            this._coin = 0;
        }
        else {
            this._coin = parseInt(value);
        }
        this.pixiManager && this.pixiManager.updateCoin(this._coin);
    }

    // Current skin of the game
    get CURRENT_SKIN() {
        return this._currentSkin;
    }
    set CURRENT_SKIN(value) {
        this._currentSkin = value;
        this.changeSkin(this._currentSkin);
    }

    changeSkinByBtn(value, skinBtn) {
        this._currentSkin = value;
        this.changeSkin(this._currentSkin, skinBtn);
    }

    // Purchased skins of the player
    get PURCHASED_SKINS() {
        return this._purchasedSkins;
    }
    set PURCHASED_SKINS(value) {
        this._purchasedSkins = value;
    }

    // Data of the daily challenge, lastFinishDailyTime, currentDifficulty, currentDailyIndex, currentChallenge, currentProgress, challengeFinished, friendsSurpassed,
    get DAILY_CHALLENGE_DATA() {
        return this._dailyChallengeData;
    }

    set DAILY_CHALLENGE_DATA(value) {
        this._dailyChallengeData = value;
        this.updateDailyChallenge(this._dailyChallengeData);
    }

    /**
     * Public
     */
    setup() {

        // Setup the event dispatcher
        this._eventDispatcher = new THREE.EventDispatcher();

        // Setup background color
        this.gameColors = GameConfig.gameColors[Math.floor(Math.random() * GameConfig.gameColors.length)];
        // this.gameColors = GameConfig.gameColors[1];
        this._backgroundGradient.style.background = this.gameColors.backgroundGradient;


        // Setup initial input
        this._touchValues = {
            mousePosition: { x: 0, y: 0 },
            oldPos: { x: 0, z: 0 },
            isPlayerTouch: false,
            lastTouch: { x: 0, y: 0 },
            swipeSpeed: 0,
        };

        // Setup all the components
        // Renderer of the game
        this.renderer = this._setupRenderer();
        // Scene of the game
        this.scene = new THREE.Scene();
        // Camera of the game
        this.camera = this._setupCamera();

        // Sound manager of the game
        this.soundManager = new SoundManager();
        // Haptic manager of the game
        this.hapticManager = new HapticManager();
        // Ui manager of the game
        this.uiManager = new UiManager();

        if (this._isMobile && window.innerWidth >= window.innerHeight) {
            console.log("show ico");
            this.uiManager.orientationPop = true;
        } else {
            this.uiManager.orientationPop = false;
        }


        // Ball of the game
        this.ball = null;
        // Platforms of the game
        this.platforms = null;



        // Stage manager of the game
        this.stageManager = new StageManager();
        // this.scene.add(this.stageManager);
        this.stageManager.setup();






        // Add facebook button
        // this._addFacebookButtons();

        // Setup debug function
        if (this._gameStatus.isDebug) {
            // this._setupOrbitControls();
            this._setupStats();
        }

        // Resize the game
        this.resize();

        // Setup envent listeners
        this._setupEventListeners();

        // Download the player data
        // this.downloadPlayerData();

        // Start engine
        gsap.ticker.fps(this.GAME.FPS);
        gsap.ticker.add(this.update);

        // DeltaTime of this frame
        this.deltaTime = 0;


    }

    setDatas() {

        this.downloadPlayerData();
        this.pixiManager = new PixiManager();
        this.pixiManager.setup();

        this.resize();

    }

    update(time, deltaTime, frame) {
        //console.log("deltaTime: " + deltaTime);
        this.deltaTime = deltaTime;

        // Update pixi scene 
        //!\\ Dont remove from here, leaderBorad needs update even after "isPaused"
        this.pixiManager && this.pixiManager.update();

        if (this.GAME.isPaused) return;

        if (this.GAME.isLose) return;

        // Render the scene
        this.renderer.render(this.scene, this.camera);

        // Update all the components in the scene
        this.stageManager.update();

        if (this._touchValues.isPlayerTouch && this._keyPressed) {
            console.log("direction:", this._direction);

            this._acc += .3;
            if (this._acc > 5) {
                this._acc = 5;
            }

            // Control the platform rotation
            if (this.GAME.isLose || this.GAME.isPaused || config.losePlatformTouched) return;
            this.platforms.rotation.y += (this._direction == "left" ? 1 : -1) * (GameConfig.keyRotateSpeed + this._acc) * GameConfig.rotateSpeed;

        }
    }

    resume() {
        this.platforms.resume();

        this.GAME.isPaused = false;
    }

    pause() {
        this.platforms.pause();
        this.soundManager && this.soundManager.pauseAllSounds();

        this.GAME.isPaused = true;
    }

    restart() {

        console.log("restart");

        this.changeColor();

        GameConfig.movementCircleDuration = GameConfig.initMovementCircleDuration;
        GameConfig.gravitationalAcceleration = GameConfig.initGravitationalAcceleration;


        this.SCORE = 0;
        this._touchEndHandler();

        this.stageManager.restart();
        this.platforms.restart();
        this.pixiManager.restart();

        this._circleSvg.classList.remove("active");

        this.GAME.isLose = false;
        this.GAME.isRetryClick = false;
        this.GAME.isFirstLose = true;

        clearTimeout(this._retryTimeOut);

        setTimeout(() => {
            this.GAME.isFirstClick = false;
            this._scoreContainer.style.display = "block";
        }, 300);


        config.losePlatformTouched = false;
        this.ball.beginBounceAnimation();
    }

    resize() {

        if (this._isMobile) {
            if (window.innerWidth >= window.innerHeight) {
                this.uiManager.orientationPop = true;
            } else {
                this.uiManager.orientationPop = false;
            }
        }

        // Save the current width and height
        this._width = window.innerWidth;
        this._height = window.innerHeight >= 450 ? window.innerHeight : 450;

        // Configurate the canvas
        this._canvas.width = this._width;
        this._canvas.height = this._height;

        // Configurate the pixi manager
        this.pixiManager && this.pixiManager.resize(this._width, this._height, this._height / 770);

        // Configurate the renderer
        if (this.camera) {
            this.camera.aspect = this._width / this._height;
            this.camera.updateProjectionMatrix();
        }
        this.renderer && this.renderer.setSize(this._width, this._height);
        if (window.innerHeight >= window.innerWidth ) {
            document.getElementById("banner-landscape-1").style.display = "none";
            document.getElementById("banner-landscape-2").style.display = "none";
        } 
            
        if(window.innerHeight < 500 ){
            document.getElementById("banner-portrait").style.display = "none";
        }else{
            document.getElementById("banner-portrait").style.display = "block";
        }
        this.H5SDK.getPlatform().getAdsModule().requestBannerAd();

    }

    /**
     * Private
     */
    async downloadPlayerData() {
        // Download the player data from the server
        const savedData = config.savedData;
        const bestScore = config.bestScore;
        console.log("Data downloaded", bestScore, savedData.coin, savedData.currentSkin, savedData.purchasedSkins, savedData.dailyChallengeData);
        if (bestScore == null) {
            console.log("Error reading the best score");
        }
        else {
            GameConfig.startedBestScore = bestScore;
            this.BEST_SCORE = bestScore;
        }

        if (savedData.coin == null) {
            this.COIN = 0;
        }
        else {
            this.COIN = savedData.coin;
        }

        if (savedData.currentSkin == null) {
            this.CURRENT_SKIN = "";
        }
        else {
            this.CURRENT_SKIN = savedData.currentSkin;
        }

        if (savedData.purchasedSkins == null) {
            this.PURCHASED_SKINS = [];
        }
        else {
            this.PURCHASED_SKINS = savedData.purchasedSkins;
        }


        if (savedData.dailyChallengeData == null) {
            this.DAILY_CHALLENGE_DATA = {
                // lastFinishDailyTime: Date.now(),
                currentDifficulty: 0,
                currentDailyIndex: 0,
                missionIndex: 0,
                currentChallenge: 0,
                currentProgress: 0,
                challengeFinished: [],
                friendsSurpassed: [],
            }
        }
        else {
            this.DAILY_CHALLENGE_DATA = savedData.dailyChallengeData;
            if (this.DAILY_CHALLENGE_DATA.missionIndex == null) {
                this.DAILY_CHALLENGE_DATA.missionIndex = 0;
            }
            //this.DAILY_CHALLENGE_DATA = savedData.dailyChallengeData;
        }

        // if (savedData.arrowBooster) {
        //     this.pixiManager.arrowItems.cpt = savedData.arrowBooster;
        // }
        // else {
        //     this.pixiManager.arrowItems.cpt = 0;
        // }


        // if (savedData.shieldBooster) {
        //     this.pixiManager.shieldItems.cpt = savedData.shieldBooster;
        // }
        // else {
        //     this.pixiManager.shieldItems.cpt = 0;
        // }



        let interval = setInterval(() => {
            if (this.pixiManager) {
                interval && clearInterval(interval);

                if (savedData.arrowBooster) {
                    this.pixiManager.arrowItems.cpt = savedData.arrowBooster;
                }
                else {
                    this.pixiManager.arrowItems.cpt = 0;
                }

                if (savedData.shieldBooster) {
                    this.pixiManager.shieldItems.cpt = savedData.shieldBooster;
                }
                else {
                    this.pixiManager.shieldItems.cpt = 0;
                }
                if (savedData.coin == null) {
                    this.COIN = 0;
                }
                else {
                    this.COIN = savedData.coin;
                }

            }
        }, 60);






    }

    async uploadPlayerData() {

        console.log(">>> uploadPlayerData ");
        await this.H5SDK.getScoreModule().setBestScore(this._bestScore);
        await this.H5SDK.getStoreModule().save("coin", this._coin);
        await this.H5SDK.getStoreModule().save("currentSkin", this._currentSkin);
        await this.H5SDK.getStoreModule().save("purchasedSkins", this._purchasedSkins);
        await this.H5SDK.getStoreModule().save("dailyChallengeData", this._dailyChallengeData);
        console.log("Data uploaded", this._bestScore, this._coin, this._currentSkin, this._purchasedSkins, this._dailyChallengeData);

        this.pixiManager && this.pixiManager.arrowItems.save();
        this.pixiManager && this.pixiManager.shieldItems.save();


    }

    async uploadEmptyPlayerData() {

        console.log(">>> uploadEmptyPlayerData ");

        await this.H5SDK.getScoreModule().setBestScore(0); // not possible, new score must be upper than older :( 
        await this.H5SDK.getStoreModule().save("coin", 0);
        await this.H5SDK.getStoreModule().save("currentSkin", "");
        await this.H5SDK.getStoreModule().save("purchasedSkins", []);
        await this.H5SDK.getStoreModule().save("dailyChallengeData", {
            // lastFinishDailyTime: Date.now(),
            currentDifficulty: 0,
            currentDailyIndex: 0,
            currentChallenge: 0,
            currentProgress: 0,
            challengeFinished: [],
            friendsSurpassed: [],
        });
        await this.H5SDK.getStoreModule().save("arrowBooster", 0);
        console.log("Empty data uploaded");

    }

    changeSkin(skinName, skinBtn) {

        if (skinName != null && this.ball != null) {
            // Informe other component to change the skin
            this.ball.changeSkin(skinName, skinBtn);
            this.platforms.changeSkin(skinName);
            let interval = setInterval(() => {
                if (this.pixiManager) {
                    interval && clearInterval(interval);
                    this.pixiManager.changeSkin(skinName);
                }
            }, 60);


        }
    }

    updateDailyChallenge(dailyChallengeData) {

        // console.log("GameManager::updateDailyChallenge:", dailyChallengeData);


        let interval = setInterval(() => {
            if (this.pixiManager) {
                interval && clearInterval(interval);
                this.pixiManager.updateDailyChallenge(dailyChallengeData);
            }
        }, 60);


    }

    notEnoughCoin() {
        // Informe other component not enough coin
        // this.pixiManager && this.pixiManager.notEnoughCoin();

        let interval = setInterval(() => {
            if (this.pixiManager) {
                interval && clearInterval(interval);
                this.pixiManager.notEnoughCoin();
            }
        }, 60);
    }

    _setupRenderer() {
        const renderer = new THREE.WebGLRenderer({
            canvas: this._canvas,
            antialias: true,
            alpha: true,
            // sortObjects: false,
        });
        // renderer.shadowMap.enabled = true;

        renderer.setPixelRatio(window.devicePixelRatio);
        return renderer;
    }

    _setupCamera() {
        const camera = new THREE.PerspectiveCamera(
            GameConfig.cameraFov,
            this._canvas.width / this._canvas.height,
            0.01,
            300,
        );

        camera.zoom = GameConfig.cameraZoom;
        camera.position.x = GameConfig.cameraX;
        camera.position.y = GameConfig.cameraY;
        camera.position.z = GameConfig.cameraZ;

        camera.lookAt(0, GameConfig.lookAtY, 0);

        // camera.lookAt(0, 110, 0);
        return camera;
    }

    _setupEventListeners() {


        this._eventDispatcher.addEventListener("gameFirstLose", () => this._gameFirstLose());

        this._eventDispatcher.addEventListener("gameNormalLose", () => this._gameNormalLose());

        this._retryVideo.addEventListener("click", () => this._showPreloaded());

        this._playText.addEventListener("click", () => this._endScreenHandler());

        this._noVideoCTA.addEventListener("click", () => this._abortBonusHandler());

        document.body.addEventListener(
            "touchstart",
            (e) => this._touchStartHandler(e),
            false,
        );

        document.body.addEventListener(
            "touchmove",
            (e) => this._touchMoveHandler(e),
            false,
        );

        document.body.addEventListener(
            "touchend",
            (e) => this._touchEndHandler(e),
            false,
        );

        document.body.addEventListener(
            "mousedown",
            (e) => this._touchStartHandler(e),
            false,
        );

        document.body.addEventListener(
            "mousemove",
            (e) => this._touchMoveHandler(e),
            false,
        );

        document.body.addEventListener(
            "mouseup",
            (e) => this._touchEndHandler(e),
            false,
        );

        document.body.addEventListener(
            "mouseleave",
            (e) => this._touchEndHandler(e),
            false,
        );

        document.body.addEventListener('mousedown', this._onDown, false);
        document.body.addEventListener('mousemove', this._onMove, false);
        document.addEventListener("keydown", this._keydown, false);
        document.addEventListener("keyup", this._keyup, false);


        window.focus();
        document.body.addEventListener('click', function (e) {
            window.focus();
        }, false);

    }

    _onDown(event) {
        event.preventDefault();
        this._setMouse(event);
    }

    _onMove(event) {
        this._setMouse(event);
    }

    _setMouse(event) {
        if (event.touches) {
            config.mouse.x = event.touches[0].clientX;
            config.mouse.y = event.touches[0].clientY;
        } else {
            config.mouse.x = event.clientX;
            config.mouse.y = event.clientY;
        }
    }

    _keydown(e) {

        // console.log("keyCode:", e.keyCode);

        if (this._currentCode == e.keyCode) {
            return;
        }
        this._currentCode = e.keyCode;
        //a
        if (e.keyCode == 65) {
            this._acc = 0;
            this._touchStartHandler();
            this._keyPressed = true;
            this._direction = "left";
        }
        //d
        if (e.keyCode == 68) {
            this._acc = 0;
            this._touchStartHandler();
            this._keyPressed = true;
            this._direction = "right";
        }
        //f
        if (e.keyCode == GameConfig.shieldKeyCode) {
            this.pixiManager.shieldItems.onDown();
        }
        //c
        if (e.keyCode == GameConfig.arrowKeyCode) {
            this.pixiManager.arrowItems.onDown();
        }
        //left
        if (e.keyCode == 37) {
            this._acc = 0;
            this._touchStartHandler();
            this._keyPressed = true;
            this._direction = "left";
        }
        //right
        if (e.keyCode == 39) {
            this._acc = 0;
            this._touchStartHandler();
            this._keyPressed = true;
            this._direction = "right";
        }

    }


    _keyup(e) {
        this._currentCode = null;
        //a
        if (e.keyCode == 65) {
            this._acc = 0;
            this._touchEndHandler();
            this._keyPressed = false;
            this._direction = false;
        }
        //d
        if (e.keyCode == 68) {
            this._acc = 0;
            this._touchEndHandler();
            this._keyPressed = false;
            this._direction = false;
        }
        //f
        if (e.keyCode == GameConfig.shieldKeyCode) {
            this.pixiManager.shieldItems.onUp();
        }
        //c
        if (e.keyCode == GameConfig.arrowKeyCode) {
            this.pixiManager.arrowItems.onUp();
        }
        //left
        if (e.keyCode == 37) {
            this._acc = 0;
            this._touchEndHandler();
            this._keyPressed = false;
            this._direction = false;
        }
        //right
        if (e.keyCode == 39) {
            this._acc = 0;
            this._touchEndHandler();
            this._keyPressed = false;
            this._direction = false;
        }

    }

    _touchStartHandler(e) {

        if (config.skinOpen) {
            return;
        }

        console.log("_touchStartHandler");

        if (!this.GAME.isFirstClick) {
            // Invoked each time the game is first clicked
            this.GAME.isFirstClick = true;
            this._startScreen.classList.add("hidden");
            this.soundManager.playSound({ name: "blank", volume: 0.005, loop: false, detuneValue: 0 });
            console.log("Blank soundddd")
            // this.pixiManager && this.pixiManager.notifyLevelStart();

            let interval = setInterval(() => {
                if (this.pixiManager) {
                    interval && clearInterval(interval);
                    this.pixiManager.notifyLevelStart();
                }
            }, 60);

            this.H5SDK.getPlatform().notifyLevelStart(0);
        }

        config.gameStarted = true;
        this.soundManager._isBlur = false;
        this._touchValues.isPlayerTouch = true;

        if (e) {
            this._touchValues.mousePosition.x = e.touches ? e.touches[0].pageX : e.clientX;
            this._touchValues.mousePosition.y = e.touches ? e.touches[0].pageY : e.clientY;

            this._touchValues.oldPos.x = e.touches ? e.touches[0].pageX : e.pageX;
            this._touchValues.oldPos.y = e.touches ? e.touches[0].pageY : e.pageY;
        }
    }

    _touchMoveHandler(e) {
        if (!this._touchValues.isPlayerTouch) return;

        this._touchValues.mousePosition.x = e.touches ? e.touches[0].pageX : e.pageX;
        this._touchValues.mousePosition.y = e.touches ? e.touches[0].pageY : e.pageY;

        if (this._touchValues.mousePosition.x < this._touchValues.oldPos.x && this._touchValues.isPlayerTouch) {
            this._touchValues.swipeSpeed = this._touchValues.mousePosition.x - this._touchValues.oldPos.x;
        } else if (this._touchValues.mousePosition.x > this._touchValues.oldPos.x && this._touchValues.isPlayerTouch) {
            this._touchValues.swipeSpeed = this._touchValues.mousePosition.x - this._touchValues.oldPos.x;
        }

        this._touchValues.oldPos.x = this._touchValues.mousePosition.x;
        this._touchValues.oldPos.y = this._touchValues.mousePosition.y;

        // Control the platform rotation
        if (this.GAME.isLose || this.GAME.isPaused || config.losePlatformTouched) return;
        //if (this.GAME.isLose || this.GAME.isPaused) return;
        this.platforms.rotation.y += this._touchValues.swipeSpeed * GameConfig.rotateSpeed;
    }

    _touchEndHandler() {
        this._touchValues.isPlayerTouch = false;

        this._touchValues.oldPos.x = 0;
        this._touchValues.oldPos.y = 0;
        this._touchValues.mousePosition.x = 0;
        this._touchValues.mousePosition.y = 0;
        this._touchValues.swipeSpeed = 0;
    }

    _gameFirstLose() {
        // When the game is lost the first time
        console.log("game first Lose");
        this._touchEndHandler();
        this.GAME.isFirstLose = false;
        this.GAME.isLose = true;
        config.gameStarted = false;

        // Communicate the level end event
        // this.H5SDK.getPlatform().notifyLevelEnd(0, this.SCORE, false);


        // Upload the player data
        this.uploadPlayerData();

        // Begin the retry process
        this._retry();
    }

    async _retry() {

        this._touchEndHandler();

        this.H5SDK.getPlatform().adTimeout = 1500;
        if (this.H5SDK.getAdsModule().hasPreloadedRewardedAd()) {
            this._retryScreen.classList.remove("hidden");
            this._circleSvg.classList.add("active");
            this._retryTimeOut = setTimeout(this._noAdHandler, 9000);
        } else {
            this.H5SDK.getAdsModule().preloadRewardedAd()
                .then((onPreloaded) => {
                    if (onPreloaded) {

                        // Show the retry button
                        this._retryScreen.classList.remove("hidden");
                        this._circleSvg.classList.add("active");
                        this._retryTimeOut = setTimeout(
                            this._noAdHandler,
                            9000,
                        );
                    } else {
                        this._noAdHandler();
                    }
                })
                .catch(() => {
                    this._noAdHandler();
                });
        }


    }

    _gameNormalLose() {
        // When the game is lost the second time, or first time by clicking the no thanks button
        console.log("game normal Lose");
        this._touchEndHandler();
        this._isGameOver = true;
        clearTimeout(this._retryTimeOut);
        this.GAME.isFirstLose = true;
        this.GAME.isLose = true;
        config.gameover = true;
        config.gameStarted = false;



        // this.pixiManager && (this.pixiManager.arrowItems.visible = false);
        // this.pixiManager && (this.pixiManager.shieldItems.visible = false);

        /* SHOW OLD LEADERBOARD*/
        // this._endScreen.classList.remove("hidden");
        // this._scoreContainer.classList.add("variant-end-screen");

        // Assign score
        this._newBestScore.innerHTML = this._score;

        // Communicate the level end event
        this.H5SDK.getPlatform().notifyLevelEnd(0, this.SCORE, false);

        // Upload the player data
        this.uploadPlayerData();

        // Show interstitial ad
        /*   if(this._nbGamePlayed%2==0){
               this._showInterstitialAd();
           }    
           else
               this._showRewardedInterstitialAd();*/
        this._showLeadeBoard();
        this._nbGamePlayed++;
        this._circleSvg.classList.remove("active");



        // this.changeColor();

    }

    _rewardHandler(onRewarded) {
        console.log("_rewardHandler (onRewarded) ", onRewarded);
        if (onRewarded) {
            this.GAME.isRetryClick = true;
            this.GAME.isLose = false;
            this.platforms.retry();
            this._retryScreen.classList.add("hidden");

            config.losePlatformTouched = false;
            this.ball.beginBounceAnimation();

        } else {
            this._abortBonusHandler();
        }
        this._retryVideo.style.display = 'block';
    }

    _abortBonusHandler() {
        console.log("_abortBonusHandler");
        if (this.GAME.isRetryClick) return;
        this._retryScreen.classList.add("hidden");
        this.platforms.retry();
        this._gameNormalLose();
    }

    _showPreloaded() {
        if(this._lockRetry) return;

        this._lockRetry = true
        clearTimeout(this._retryTimeOut);
        this._retryVideo.style.display = 'none';
        setTimeout(() => {
            this._lockRetry = false;
        }, 2500);
        this.H5SDK.getAdsModule().showPreloadedRewardedAd()
            .then(this._rewardHandler)
            .catch(console.log);
    }

    _noAdHandler() {
        //console.log("_noAdHandler");
        if (this.GAME.isRetryClick) return;
        this._retryScreen.classList.add("hidden");
        this.platforms.retry();
        this._gameNormalLose();
    }

    _showInterstitialAd() {
        console.log("_showInterstitialAd");
        this.H5SDK.getAdsModule().requestInterstitialAd().then(() => {
            if (this.H5SDK.getPlatform().isFacebook()) {
                this.H5SDK.getTournamentModule().showTournamentCreatorPopup()
                    .then(() => {

                        this._showLeadeBoard();

                    });
            } else {
                this._showLeadeBoard();
            }
        });
    }

    async _showRewardedInterstitialAd() {
        console.log("_showRewardedInterstitialAd");
        const riOk = await this.H5SDK.getAdsModule().requestRewardedInterstitialAd();
        if (riOk) {
            if (Math.floor(Math.random() * 2) == 1)
                this.pixiManager.arrowItems.add();
            else
                this.pixiManager.shieldItems.add();

            this.pixiManager.updateCoin(this.COIN + 60);
            setTimeout(() => {

                if (this.H5SDK.getPlatform().isFacebook()) {
                    this.H5SDK.getTournamentModule().showTournamentCreatorPopup()
                        .then(() => {
                            this._showLeadeBoard();
                        });
                } else {
                    this._showLeadeBoard();
                }

            }, 1500);
        } else {
            if (this.H5SDK.getPlatform().isFacebook()) {
                this.H5SDK.getTournamentModule().showTournamentCreatorPopup()
                    .then(() => {
                        this._showLeadeBoard();
                    });
            } else {
                this._showLeadeBoard();
            }
        }
    }



    _showLeadeBoard() {
        //this.H5SDK.getAdsModule().requestInterstitialAd().then(() => {
        this._endScreen.classList.add("hidden");
        this._scoreContainer.style.display = "none";

        console.log("Leader board here");
        console.log(this._score, this._bestScore);

        // if (this.pixiManager) {
        //     this.pixiManager.leaderBoard.score = this._score;
        //     this.pixiManager.leaderBoard.bestScoreValue = this._bestScore;
        //     this.pixiManager.leaderBoard.show();
        // }

        let interval = setInterval(() => {
            if (this.pixiManager) {
                interval && clearInterval(interval);
                this.pixiManager.leaderBoard.score = this._score;
                this.pixiManager.leaderBoard.bestScoreValue = this._bestScore;
                this.pixiManager.leaderBoard.show();
            }
        }, 60);
        // });


    }

    _endScreenHandler() {
        console.log("_endScreenHandler");

        this._endScreen.classList.add("hidden");
        this._scoreContainer.classList.remove("variant-end-screen");
        this._circleSvg.classList.remove("active");
        this._startScreen.classList.remove("hidden");

        this.restart();
    }

    _setupStats() {
        this.stats = new Stats();
        this.stats.showPanel(0);
        this.stats.dom.style.position = "absolute";
        this.stats.dom.style.left = "100px";
        document.body.appendChild(this.stats.dom);

        this.nbpolyDiv = document.createElement("div");
        this.nbpolyDiv.style.position = "absolute";
        this.nbpolyDiv.style.bottom = "100px";
        this.nbpolyDiv.style["font-family"] = "arial";
        this.nbpolyDiv.style["text-shadow"] = "0px 1px black, 1px 0px black, 1px 1px black, 0px 0px black";
        document.body.appendChild(this.nbpolyDiv);
        this.drawDiv = document.createElement("div");
        document.body.appendChild(this.drawDiv);
        this.drawDiv.style.position = "absolute";
        this.drawDiv.style.bottom = "80px";
        this.drawDiv.style["font-family"] = "arial";
        this.drawDiv.style["text-shadow"] = "0px 1px black, 1px 0px black, 1px 1px black, 0px 0px black";

        // const axesHelper = new THREE.AxesHelper(10);
        // this.scene.add(axesHelper);
    }

    _addFacebookButtons() {
        const isFacebook = this.H5SDK.getPlatform().isFacebook();

        const inviteBtn = document.querySelector(".invite-friends-button");
        const shareBtn = document.querySelector(".share-friends-button");

        // If we are on facebook then ...
        if (isFacebook) {
            // show buttons
            inviteBtn.style.display = "flex";
            shareBtn.style.display = "flex";

            // init listeners
            shareBtn.addEventListener("click", () => {
                this.H5SDK.getPlatform().shareWithFriends(
                    AssetsManager.base64Picture,
                    `My best score is ${this.SCORE}. Try to beat me !`,
                );
            });

            inviteBtn.addEventListener("click", () => {
                this.H5SDK.getPlatform().inviteFriends(
                    AssetsManager.base64Picture,
                    `My best score is ${this.SCORE}. Can you do better ?`,
                );
            });
        }
    }



    changeColor() {

        this.gameColors = GameConfig.gameColors[Math.floor(Math.random() * GameConfig.gameColors.length)];
        this._backgroundGradient.style.background = this.gameColors.backgroundGradient;

        this.ball.updateColor();
        this.platforms.updateColor();
        this.tube.updateColor();

        console.log("changeColor");

    }


}

export default new GameManager();
